import React from 'react';
import { Megaphone } from 'lucide-react';
import { ILMSAlert } from '../../../components/ILMSAlert';
import { ILMSFormField, ILMSInput, ILMSSelect, ILMSTextarea } from '../../../components/ILMSForm';
import { Marquee } from '../../../public/components/Motion';
import '../../../public/motion.css';
import { useQuery } from '@tanstack/react-query';
import { SITE_DEFAULTS, getVisits } from '../../../utils/site';
import { SaveBar } from './SaveBar';
import { useSiteDraft } from './useSiteDraft';

const SPEEDS = [
  { value: 20, label: 'Fast' }, { value: 30, label: 'Quick' }, { value: 45, label: 'Normal' }, { value: 60, label: 'Slow' }, { value: 90, label: 'Very slow' },
];
const lines = (text: string) => text.split('\n');

// The scrolling news on the website, a scrolling notice inside the app, the
// website's main headline, the login page line and the contact details.
export function WebsitePanel() {
  const { draft, set, setDraft, save, saving, error, saved, dirty, canEdit } = useSiteDraft();
  const visits = useQuery({ queryKey: ['site-visits'], queryFn: getVisits, refetchInterval: 60_000 });
  const items = (v: string[]) => v.join('\n');
  const tidy = (v: string[]) => v.map((s) => s.trim()).filter(Boolean);

  const ticker = (label: string, list: string[], seconds: number, dark: boolean) => (
    <div className={dark ? 'bg-brand-green-900 text-brand-green-50 rounded-md overflow-hidden' : 'bg-brand-gold-50 text-brand-gold-900 border border-brand-gold-200 rounded-md overflow-hidden'}>
      <div className="flex items-center">
        {dark && (
          <span className="z-10 flex shrink-0 items-center gap-1.5 bg-brand-gold-400 px-3 py-2 text-xs font-bold uppercase tracking-wide text-brand-green-950">
            <Megaphone className="h-3.5 w-3.5" /> {label || 'News'}
          </span>
        )}
        <Marquee seconds={seconds} className="flex-1 py-2">
          {(tidy(list).length ? tidy(list) : ['(nothing to show)']).map((t, i) => <span key={i} className="px-6 text-sm">{t}</span>)}
        </Marquee>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col gap-6">
      {error && <ILMSAlert tone="critical">{error}</ILMSAlert>}

      <section className="flex flex-col gap-3">
        <h3 className="text-card-title">Website — scrolling news bar</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ILMSFormField label="Show the news bar" htmlFor="news_show">
            <ILMSSelect id="news_show" disabled={!canEdit} value={draft.news_show ? 'yes' : 'no'} onChange={(e) => set('news_show', e.target.value === 'yes')}>
              <option value="yes">Show</option><option value="no">Hide</option>
            </ILMSSelect>
          </ILMSFormField>
          <ILMSFormField label="Label" htmlFor="news_label">
            <ILMSInput id="news_label" disabled={!canEdit} maxLength={20} value={draft.news_label} onChange={(e) => set('news_label', e.target.value)} />
          </ILMSFormField>
          <ILMSFormField label="Speed" htmlFor="news_seconds">
            <ILMSSelect id="news_seconds" disabled={!canEdit} value={draft.news_seconds} onChange={(e) => set('news_seconds', Number(e.target.value))}>
              {SPEEDS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </ILMSSelect>
          </ILMSFormField>
        </div>
        <ILMSFormField label="News — one line each" htmlFor="news_items" helperText="Keep each line short. Up to 30 lines.">
          <ILMSTextarea id="news_items" rows={5} disabled={!canEdit} value={items(draft.news_items)} onChange={(e) => set('news_items', lines(e.target.value))} />
        </ILMSFormField>
        {draft.news_show && ticker(draft.news_label, draft.news_items, draft.news_seconds, true)}
      </section>

      <section className="flex flex-col gap-3">
        <h3 className="text-card-title">Inside the app — scrolling notice for staff</h3>
        <p className="text-secondary text-ink-500 -mt-2">Shows under the top bar on every page of the web app, e.g. "Vet visit on Friday 10 AM".</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ILMSFormField label="Show the notice" htmlFor="app_notice_show">
            <ILMSSelect id="app_notice_show" disabled={!canEdit} value={draft.app_notice_show ? 'yes' : 'no'} onChange={(e) => set('app_notice_show', e.target.value === 'yes')}>
              <option value="no">Hide</option><option value="yes">Show</option>
            </ILMSSelect>
          </ILMSFormField>
          <ILMSFormField label="Speed" htmlFor="app_notice_seconds">
            <ILMSSelect id="app_notice_seconds" disabled={!canEdit} value={draft.app_notice_seconds} onChange={(e) => set('app_notice_seconds', Number(e.target.value))}>
              {SPEEDS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </ILMSSelect>
          </ILMSFormField>
        </div>
        <ILMSFormField label="Notice — one line each" htmlFor="app_notice_items">
          <ILMSTextarea id="app_notice_items" rows={3} disabled={!canEdit} value={items(draft.app_notice_items)} onChange={(e) => set('app_notice_items', lines(e.target.value))} />
        </ILMSFormField>
        {draft.app_notice_show && ticker('', draft.app_notice_items, draft.app_notice_seconds, false)}
      </section>

      <section className="flex flex-col gap-3">
        <h3 className="text-card-title">Website — home page headline</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ILMSFormField label="Small line above the headline" htmlFor="hero_badge">
            <ILMSInput id="hero_badge" disabled={!canEdit} maxLength={80} value={draft.hero_badge} onChange={(e) => set('hero_badge', e.target.value)} />
          </ILMSFormField>
          <ILMSFormField label="Headline" htmlFor="hero_title">
            <ILMSInput id="hero_title" disabled={!canEdit} maxLength={140} value={draft.hero_title} onChange={(e) => set('hero_title', e.target.value)} />
          </ILMSFormField>
        </div>
        <ILMSFormField label="Text under the headline" htmlFor="hero_text">
          <ILMSTextarea id="hero_text" rows={3} disabled={!canEdit} maxLength={500} value={draft.hero_text} onChange={(e) => set('hero_text', e.target.value)} />
        </ILMSFormField>
        <ILMSFormField label="Login page — big line" htmlFor="login_tagline">
          <ILMSInput id="login_tagline" disabled={!canEdit} maxLength={120} value={draft.login_tagline} onChange={(e) => set('login_tagline', e.target.value)} />
        </ILMSFormField>
      </section>

      <section className="flex flex-col gap-3">
        <h3 className="text-card-title">Website — visitor count</h3>
        <p className="text-secondary text-ink-500 -mt-2">
          Each visit to the home page is counted once per browser visit; a phone or computer seen for the first time is also a new visitor. No personal details are kept.
        </p>
        {visits.data && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[['Visits so far', visits.data.views], ['Visitors so far', visits.data.visitors], ['Visits today', visits.data.today_views], ['New visitors today', visits.data.today_visitors]].map(([label, n]) => (
              <div key={label} className="rounded-md border border-ink-300/20 bg-surface-subtle p-3">
                <p className="text-helper-text text-ink-500">{label}</p>
                <p className="text-section-title font-bold tabular-nums">{Number(n).toLocaleString('en-IN')}</p>
              </div>
            ))}
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ILMSFormField label="Show the count on the home page" htmlFor="visitors_show" helperText="Hidden = still counted, only you see it here.">
            <ILMSSelect id="visitors_show" disabled={!canEdit} value={draft.visitors_show ? 'yes' : 'no'} onChange={(e) => set('visitors_show', e.target.value === 'yes')}>
              <option value="yes">Show</option><option value="no">Hide</option>
            </ILMSSelect>
          </ILMSFormField>
          <ILMSFormField label="Label under the number" htmlFor="visitors_label">
            <ILMSInput id="visitors_label" disabled={!canEdit} maxLength={40} value={draft.visitors_label} onChange={(e) => set('visitors_label', e.target.value)} />
          </ILMSFormField>
        </div>
      </section>

      <section className="flex flex-col gap-3">
        <h3 className="text-card-title">Contact details (website footer and Contact page)</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ILMSFormField label="Phone" htmlFor="phone">
            <ILMSInput id="phone" disabled={!canEdit} maxLength={30} value={draft.phone} onChange={(e) => set('phone', e.target.value)} />
          </ILMSFormField>
          <ILMSFormField label="Email" htmlFor="email">
            <ILMSInput id="email" type="email" disabled={!canEdit} maxLength={120} value={draft.email} onChange={(e) => set('email', e.target.value)} />
          </ILMSFormField>
          <ILMSFormField label="Address" htmlFor="address">
            <ILMSTextarea id="address" rows={2} disabled={!canEdit} maxLength={250} value={draft.address} onChange={(e) => set('address', e.target.value)} />
          </ILMSFormField>
          <ILMSFormField label="Business hours" htmlFor="business_hours" helperText='e.g. "Mon–Sat, 8:00 AM – 6:00 PM". Blank = "Please call ahead".'>
            <ILMSInput id="business_hours" disabled={!canEdit} maxLength={120} value={draft.business_hours} onChange={(e) => set('business_hours', e.target.value)} />
          </ILMSFormField>
        </div>
      </section>

      <SaveBar
        canEdit={canEdit} dirty={dirty} saving={saving} saved={saved}
        onSave={save /* the server drops blank lines */}
        onReset={() => setDraft((d) => ({
          ...d,
          ...Object.fromEntries((['news_show', 'news_label', 'news_items', 'news_seconds', 'app_notice_show', 'app_notice_items', 'app_notice_seconds', 'hero_badge', 'hero_title', 'hero_text', 'login_tagline', 'address', 'phone', 'email', 'business_hours', 'visitors_show', 'visitors_label'] as const).map((k) => [k, SITE_DEFAULTS[k]])),
        }))}
      />
    </div>
  );
}
